/**
 * SaltieShoutOutArchive — a warm, quiet gallery of every past
 * Saltie Shout-Out. Sits below the featured letter on the
 * /saltie-shout-outs page and lets readers return to the letters
 * that stopped them in their tracks.
 *
 * Each past letter appears as a small painterly card showing:
 *   • the recipient's name (large)
 *   • the closing date (small caps eyebrow)
 *   • the first breath of the message (a truncated preview)
 *   • a soft "open the letter" prompt
 *
 * Clicking any card opens that letter inside the same peaceful
 * shutter-reveal modal used for the featured letter. The whole
 * archive reads like flipping through a keepsake book, not a
 * database list.
 */
import { useEffect, useState } from "react";
import { listSaltieShoutOutArchive } from "../lib/api";
import SaltieShoutOutReveal from "./SaltieShoutOutReveal";

const PREVIEW_LEN = 140;

function previewOf(body) {
  if (!body) return "";
  const flat = body.replace(/\s+/g, " ").trim();
  if (flat.length <= PREVIEW_LEN) return flat;
  const cut = flat.slice(0, PREVIEW_LEN);
  const lastSpace = cut.lastIndexOf(" ");
  return `${lastSpace > 60 ? cut.slice(0, lastSpace) : cut}\u2026`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
  } catch {
    return "";
  }
}

function ArchiveCard({ letter, onOpen }) {
  const eyebrow = letter.closing_date || formatDate(letter.published_at);
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/saltie-shout-outs/archive/${letter.id}/pdf`;
  return (
    <div className="relative">
    <button
      type="button"
      onClick={() => onOpen(letter)}
      data-testid={`archive-card-${letter.id}`}
      className="group relative text-left w-full appearance-none bg-transparent border-0 p-0 cursor-pointer focus:outline-none"
      aria-label={`Open the Saltie Shout-Out to ${letter.recipient}`}
    >
      <div
        className="relative rounded-md overflow-hidden transition-transform duration-500 ease-out group-hover:-translate-y-1 group-focus:-translate-y-1"
        style={{
          background:
            "radial-gradient(ellipse at 50% 12%, #FFF7E1 0%, #FDECC6 60%, #F6DDAC 100%)",
          padding: "1.6rem 1.6rem 1.4rem",
          boxShadow:
            "0 12px 24px -14px rgba(40,25,10,0.35), 0 4px 8px -3px rgba(40,25,10,0.2), inset 0 0 0 1px rgba(168,113,73,0.22)",
        }}
      >
        {/* Faint letterpress inner border */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-[3px]"
          style={{
            top: 8,
            left: 8,
            right: 8,
            bottom: 8,
            border: "1px solid rgba(168,113,73,0.22)",
          }}
        />
        {eyebrow ? (
          <p
            className="uppercase text-[#7E9268]"
            style={{
              fontSize: "10px",
              letterSpacing: "0.32em",
              marginBottom: "0.65rem",
            }}
          >
            {eyebrow}
          </p>
        ) : null}
        <h3
          className="text-[#3B2E24]"
          style={{
            fontFamily: "'Bakerie', 'Cormorant Garamond', serif",
            fontSize: "clamp(1.35rem, 2.1vw, 1.75rem)",
            lineHeight: 1.15,
            marginBottom: "0.7rem",
          }}
        >
          To {letter.recipient}
        </h3>
        <p
          className="text-[#3B2E24]"
          style={{
            fontFamily: "'Bakerie', 'Cormorant Garamond', serif",
            fontSize: "0.98rem",
            lineHeight: 1.6,
            color: "#3B2E24",
          }}
        >
          {previewOf(letter.body)}
        </p>
        <p
          className="mt-4 uppercase text-[#8B6F4E]"
          style={{
            fontSize: "10px",
            letterSpacing: "0.34em",
          }}
        >
          &mdash; open the letter &mdash;
        </p>
      </div>
    </button>
    <p className="text-center mt-2">
      <a
        href={pdfUrl}
        onClick={(e) => e.stopPropagation()}
        data-testid={`archive-download-pdf-${letter.id}`}
        className="inline-block text-[10px] uppercase tracking-[0.28em] text-[#8B6F4E] hover:text-[#4B3A2F] transition-colors"
      >
        download the keepsake
      </a>
    </p>
    </div>
  );
}

export default function SaltieShoutOutArchive() {
  const [letters, setLetters] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listSaltieShoutOutArchive()
      .then((d) => {
        if (!cancelled) setLetters(Array.isArray(d.letters) ? d.letters : []);
      })
      .catch(() => {
        if (!cancelled) setLetters([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Adapt archive letter (snake_case) to stationery props (camelCase).
  const revealProps = selected
    ? {
        recipient: selected.recipient,
        body: selected.body,
        photoUrl: selected.photo_url || null,
        photoAlt: selected.photo_alt || "",
        closingDate:
          selected.closing_date ||
          formatDate(selected.published_at) ||
          "",
      }
    : null;

  if (letters === null) {
    return (
      <section
        className="max-w-4xl mx-auto px-6 pb-16 text-center"
        data-testid="sso-archive-loading"
      >
        <p
          className="font-script text-[#7E9268]"
          style={{ fontSize: "0.95rem" }}
        >
          opening the keepsake book&hellip;
        </p>
      </section>
    );
  }

  if (letters.length === 0) return null;

  return (
    <section
      className="max-w-5xl mx-auto px-6 pt-8 pb-24"
      aria-labelledby="sso-archive-heading"
      data-testid="sso-archive-section"
    >
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.32em] text-[#7E9268] mb-3">
          The keepsake book
        </p>
        <h2
          id="sso-archive-heading"
          className="font-display text-[#4B3A2F]"
          style={{ fontSize: "clamp(1.7rem, 3vw, 2.3rem)", lineHeight: 1.15 }}
        >
          Past Saltie Shout-Outs
        </h2>
        <div className="gold-rule mx-auto my-5" style={{ width: "4rem" }} />
        <p
          className="font-body text-[#2C2724]/85 mx-auto"
          style={{
            fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)",
            lineHeight: 1.7,
            maxWidth: "34rem",
          }}
        >
          Every letter written from this desk is kept here &mdash; so the
          words that stopped you in your tracks can always be returned to.
        </p>
      </div>

      <div
        className="grid gap-6 sm:gap-8"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
      >
        {letters.map((letter) => (
          <ArchiveCard
            key={letter.id}
            letter={letter}
            onOpen={setSelected}
          />
        ))}
      </div>

      <SaltieShoutOutReveal
        open={!!selected}
        onClose={() => setSelected(null)}
        shoutOut={revealProps || {}}
      />
    </section>
  );
}
